import * as React from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { SpinnerIosRegular } from '@fluentui/react-icons'
import { TooltipButton } from './TooltipButton'

interface ConfirmDialogProps {
  trigger?: React.ReactNode
  triggerTooltip?: string
  triggerTooltipSide?: 'top' | 'bottom' | 'left' | 'right'
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  onConfirm: () => void
  closeOnConfirm?: boolean
  onCancel?: () => void
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link'
  isSubmitting?: boolean
}

export function ConfirmDialog({
  trigger,
  triggerTooltip,
  triggerTooltipSide,
  isOpen: externalIsOpen,
  onOpenChange: externalOnOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  closeOnConfirm = true,
  onCancel,
  confirmVariant = 'default',
  isSubmitting = false,
}: ConfirmDialogProps) {
  const [internalIsOpen, setInternalIsOpen] = React.useState(false)

  const isOpen = externalIsOpen ?? internalIsOpen
  const onOpenChange = externalOnOpenChange ?? setInternalIsOpen

  const handleCancel = () => {
    onOpenChange(false)
    onCancel?.()
  }

  const handleConfirm = () => {
    onConfirm()
    if (closeOnConfirm) {
      onOpenChange(false)
    }
  }

  const dialogTrigger = triggerTooltip ? (
    <TooltipButton tooltip={triggerTooltip} side={triggerTooltipSide}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
    </TooltipButton>
  ) : (
    <DialogTrigger asChild>{trigger}</DialogTrigger>
  )

  // If trigger is provided, use DialogTrigger to wrap it
  // Otherwise, just render the Dialog with external control
  if (trigger) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        {dialogTrigger}
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {isSubmitting ? (
              <div className="flex w-full justify-center">
                <SpinnerIosRegular fontSize={20} className="animate-spin" />
              </div>
            ) : description ? (
              <DialogDescription>{description}</DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            {!isSubmitting && (
              <>
                <Button variant="outline" onClick={handleCancel}>
                  {cancelLabel}
                </Button>
                <Button variant={confirmVariant} onClick={handleConfirm}>
                  {confirmLabel}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  // No trigger provided, use external control
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {isSubmitting ? (
            <div className="flex w-full justify-center">
              <SpinnerIosRegular fontSize={20} className="animate-spin" />
            </div>
          ) : (
            description && <DialogDescription>{description}</DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter>
          {!isSubmitting && (
            <>
              <Button variant="outline" onClick={handleCancel}>
                {cancelLabel}
              </Button>
              <Button variant={confirmVariant} onClick={handleConfirm}>
                {confirmLabel}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
